/**
 * MBDTravel v3 bölümlerinin metinleri: Operasyon, Satış, Finans ve Katalog.
 *
 * Metin burada duruyor çünkü bu ekranın cümleleri **karar anlatıyor**, etiket değil. "Release
 * CRM'den yapılamıyor" ya da "iade gelirden düşülmüyor" cümlelerini kodun içine gömmek, bir gün
 * ifadeyi düzeltmek isteyen kişiyi `.tsx` dosyasında dize aramaya zorlar; burada hepsi yan yana
 * ve hangi ekranın ne dediği tek bakışta okunuyor.
 *
 * Sayı içeren cümleler **fonksiyon**: Türkçede sayı ile isim arasındaki ilişki İngilizcedeki gibi
 * "(s)" ekiyle çözülmüyor, cümlenin tamamının tek parça yazılması gerekiyor. Parçalara bölünmüş
 * bir cümle çeviriyi de bozar.
 *
 * Ortak kelimeler ve biçimleyiciler `./ortak` içinde; burada yalnız bu dört bölüme özgü olanlar
 * var. Terim kararları `docs/tr-sozluk.md` dosyasında.
 *
 * ## Türkçesi bilerek yazılmayanlar
 *
 * `yielding`, `release`, `saga` ve `slot` olduğu gibi bırakıldı; `authorised, not captured`
 * çifti ise Türk ödeme dilindeki karşılığıyla yazıldı: provizyon alındı, tahsil edilmedi.
 * Hepsi ticari ya da sözleşmesel terim ve karşılıkları `docs/tr-sozluk.md` içinde karara
 * bağlanmadı. Uydurulmuş bir karşılık eksik olandan kötüdür: eksik olan görünür ve sorulur,
 * uydurulmuş olan ekipte konuşulan kelimeden sessizce ayrışır.
 */

import { ORTAK } from './ortak';

export const COMMERCE_METIN = {
  /**
   * Bölüm başlığı. Ürün adı değil: ürün sahibinin kararı, ekranda yalnız "MBD Travel" adı geçer,
   * iç sürüm numarası ("v3") geçmez. Bu panel genel web sitesinin sipariş, operasyon, finans ve
   * katalog verisini okuduğu için ad da onu söylüyor.
   */
  baslik: 'Web sitesi',

  /** Sekme adları. Şişirme eki yok: "Operasyon", "Operasyon panosu" değil. */
  sekme: {
    operasyon: 'Operasyon',
    satis: 'Satış',
    finans: 'Finans',
    katalog: 'Katalog',
    /**
     * Denetim maddesi B5. Diğer dört sekmeden farkı: onlar v3'e her seferinde yeniden soruyor,
     * bu sekme v3'ün CRM'e bildirdiği ve CRM'in kendi veritabanında tuttuğu asıl kaydı okuyor
     * (`webshop.service.ts`). "Web kayıtları", "Web sitesi" başlığıyla karışmasın diye ayrı adla.
     */
    webKayitlari: 'Web kayıtları',
  },

  /** Her v3 yanıtının altındaki köken satırı: veri nereden, ne zaman, kaç denemede geldi. */
  kaynak: {
    satir: (sistem: string, adres: string, an: string, sure: string, deneme: string, korelasyon: string) =>
      `${sistem} kaynağından okundu · ${adres} · ${an} · ${sure} ms · ${deneme}. deneme · korelasyon ${korelasyon}`,
  },

  hata: {
    tanimsiz: 'Web sitesi tanımlı değil',
    okumaBasarisiz: (kod: string | number) => `Web sitesi okuması başarısız (${kod})`,
    ayarVe: 've',
    ayarTanimla: 'tanımlanmalı.',
    jetonOnce: 'Jetonu web sitesini işleten ekip, web sitesinin',
    jetonSonra:
      'listesinde her istemci için ayrı üretiyor; panel oturum çerezi kabul edilmiyor. Tanımlanana kadar hiçbir veri gösterilmiyor: bu ekran satır uydurmuyor.',
    korelasyon: (korelasyon: string) => `web sitesi ekibine korelasyon ${korelasyon} numarasını bildirin.`,
  },

  yielding: {
    baslik: 'Yielding kuyruğu',
    aciklama:
      'Ödenen sipariş kendiliğinden tedarikçi rezervasyonuna dönmüyor. Siparişler burada birikiyor ki ekip kalkış başına tek karar versin: aynı kalkışa düşen iki sipariş, indirim ya da daha yüksek komisyon istemek için pazarlık gücü, ve reddedilen otomatik bir rezervasyon parası alınmış müşteriyi yersiz bırakıyor. Dojoin bu kuyrukta hiç yok: anında teyit veriyor, beklemenin orada kazandırdığı bir şey yok. Tetikleyen tedarikçi, ödeme değil.',
    sayfaBoyu: 'Sayfa başına sipariş',
    yenile: 'Yenile',
    releaseYok: "Release CRM'den yapılamıyor.",
    eksikSayfa: (eksik: string, sinir: string, toplam: string) =>
      `Bekleyen ${eksik} sipariş bu sayfada değil. Bu kuyruk sayfalanamıyor, çünkü sayfalanınca bir grup ikiye bölünür ve birikim olduğundan az görünür: ${toplam} siparişin tamamını görmek için sınırı ${sinir} değerinin üstüne çıkarın.`,
    toplam: (kalkis: string, siparis: string) =>
      `${kalkis} kalkış release bekliyor · kuyrukta ${siparis} sipariş var`,
    kalkisTarihiYok: 'Kalkış tarihi yok',
    urunNumarali: (urunId: string) => `${urunId} numaralı ürün`,
    tedarikci: (kod: string) => `tedarikçi ${kod}`,
    /**
     * Sipariş sayısı cümlenin içinde kalın yazılıyor, o yüzden cümle sayının **sonrasından**
     * başlıyor: birikimi okuyan gözün ilk gördüğü şey sayının kendisi olmalı.
     */
    birikimSonu: (yolcu: string) => `sipariş · ${yolcu} yolcu`,
    pazarlik: ' · pazarlık: bu kalkışta birden çok sipariş var',
    sutun: {
      referans: 'Referans',
      olusturuldu: 'Oluşturuldu',
      yolcu: 'Yolcu',
      toplam: ORTAK.toplam,
    },
  },

  siparisler: {
    baslik: 'Web sitesi siparişleri',
    tumu: 'Tümü',
    kendiUrunu: 'Kendi ürünümüz',
    sagaBaslamadi: 'Başlamadı',
    evet: 'Evet',
    hayir: 'Hayır',
    sayac: (gosterilen: string, toplam: string) => `${toplam} siparişin ${gosterilen} tanesi`,
    sutun: {
      referans: 'Referans',
      durum: ORTAK.durum,
      toplam: ORTAK.toplam,
      odendi: 'Ödendi',
      yolcu: 'Yolcu',
      tedarikci: ORTAK.tedarikci,
      otomatikRezervasyon: 'Otomatik rezervasyon',
      releaseBekliyor: 'Release bekliyor',
      saga: 'Saga',
      satisKodu: 'Satış kodu',
    },
  },

  siparisDetay: {
    yolcularBaslik: 'Yolcular',
    /** Ekranda duran gizlilik notu: burada olmayan alan buradan sızamaz. */
    yolcuGizlilik:
      'Yalnız ad ve yolcu türü. Pasaport, kimlik numarası, doğum tarihi ve iletişim bilgisi bu sözleşmede bilerek yok: burada toplanmıyor, dolayısıyla buradan sızamıyor.',
    /** Karşılığı karara bağlanmadı: "aday" ile karışma riski var, o yüzden İngilizcesi duruyor. */
    liderYolcu: ' · lead passenger',
    sagaBaslik: 'Tedarikçi saga adımları',
    sagaOzeti: (durum: string, satisKodu: string, releaseAni: string) =>
      `Saga durumu ${durum} · satış kodu ${satisKodu} · release ${releaseAni}`,
    sagaAdimi: (adim: string, durum: string) => `${adim}: ${durum}`,
    sagaHatasi: (kod: string) => ` (${kod})`,
    sutun: {
      urun: 'Ürün',
      kalkis: 'Kalkış',
      slot: 'Slot',
      adet: 'Adet',
      birimFiyat: 'Satılan birim fiyat',
    },
  },

  satisRaporu: {
    baslik: 'Satış raporu',
    /**
     * Üç parça, çünkü ortadaki kelime ekranda vurgulanıyor: raporu okuyan kişinin ilk sorusu
     * "hangi an sayıldı" ve cevabı cümlenin ortasında kaybolmamalı.
     */
    aciklamaOnce: 'Siparişin oluşturulduğu an değil,',
    aciklamaVurgu: 'ödendiği',
    aciklamaSonra:
      'an sayılıyor; toplamlar sipariş kalemlerinden çıkıyor ki karışık bir sepet tek bir turu şişirmesin. İade gelirin yanında raporlanıyor, gelirden düşülmüyor. Kanal ya da satıcı kırılımı yok: web sitesi ikisini de tutmuyor ve boş bir sütun rapor değildir.',
    donem: 'Dönem',
    dil: 'Dil',
    dilAdi: { tr: 'Türkçe', en: 'İngilizce' } as Readonly<Record<string, string>>,
    paraBirimi: ORTAK.paraBirimi,
    gruplama: 'Gruplama',
    gruplamaAdi: { product: 'Ürün', departure: 'Kalkış' } as Readonly<Record<string, string>>,
    calistir: 'Raporu çalıştır',
    kartSiparis: 'Sipariş',
    kartAdet: 'Satılan adet',
    kartGelir: 'Gelir',
    kartIade: 'İade',
    gelirNotu: 'Toplamlar bütün grupları kapsıyor, yalnız bu sayfayı değil',
    iadeNotu: (siparis: string, adet: string) => `${siparis} sipariş · ${adet} adet · gelirden düşülmüyor`,
    aralik: (baslangic: string, bitis: string, gruplama: string) =>
      `${baslangic} ile ${bitis} arası, iki uç dahil; ${gruplama} bazında gruplandı`,
    icerikYok: (slug: string, dil: string) => `${slug} (${dil} içeriği yok)`,
    bos: 'Bu aralıkta ödenmiş sipariş yok. Bu rapora yalnız yayımlanmış ürünler giriyor; taslak hâldeki bir katalog ne satıyor ne de raporlanıyor.',
    sutun: {
      urun: 'Ürün',
      kalkis: 'Kalkış',
      siparis: 'Sipariş',
      adet: 'Adet',
      gelir: 'Gelir',
      iade: 'İade',
    },
  },

  finans: {
    baslik: 'Alacaklar ve faturalar',
    aciklama:
      "Bir satır bir sipariş, bir fatura değil: fatura satırı ancak ilk kesme denemesinde açılıyor, yani satırın olmaması ile faturanın kesilmemiş olması aynı şey ve NONE gerçek, süzülebilir bir durum. Belgenin kendisi, yani seri numarası, KDV ve PDF, fatura sağlayıcısına ait; iki fatura defteri er geç aynı numarayı iki kez keser.",
    faturaDurumu: 'Fatura durumu',
    tumu: 'Tümü',
    odemeKaydiYok: 'Ödeme kaydı yok',
    /** Terim çifti karara bağlanmadığı için olduğu gibi duruyor. `docs/tr-sozluk.md` dosyasına bakın. */
    /**
     * Provizyon: para bloke edildi ama **tahsil edilmedi**.
     *
     * İngilizcesi ("authorised, not captured") bırakılmıştı ve sözlüğe soru olarak yazılmıştı.
     * Karşılığı Türk ödeme dilinde yerleşik ve tek anlamlı: provizyon alınan tutar henüz
     * hesaba geçmemiş tutardır. Uydurulmuş bir karşılık değil, bankacılık terimi.
     */
    provizyonNotu: ' (provizyon alındı, tahsil edilmedi)',
    kurSatiri: (kur: string, tarih: string, kaynak: string) => `${kur} · ${tarih} · ${kaynak}`,
    sayac: (gosterilen: string, toplam: string) =>
      `${toplam} satırın ${gosterilen} tanesi. Burada yalnız ödenmiş ve iade edilmiş siparişler görünüyor.`,
    sutun: {
      referans: 'Referans',
      siparis: ORTAK.siparis,
      odeme: 'Ödeme',
      siparisToplami: 'Sipariş toplamı',
      odendi: 'Ödendi',
      fatura: 'Fatura',
      faturaTutari: 'Fatura tutarı',
      kur: 'FX (EUR/USD)',
    },
  },

  katalog: {
    baslik: 'Katalog',
    aciklama:
      'Genel web sitesinin okuduğu üç ucun aynısı. CRM katalog kopyası tutmuyor ve kendi sorgusunu çalıştırmıyor, yani neyin satışta olduğunun ikinci bir cevabı hiç oluşmuyor. Taslak hâldeki ürünler görünmüyor: bu, kaldırılacak bir süzgeç değil, yayım varsayılanının çalışması.',
    goster: 'Göster',
    sayac: (toplam: string) => `${toplam} yayımlanmış ürün`,
    kalkisFiyati: (slot: string, tutar: string) => `${slot} ${tutar}`,
    sutun: {
      ad: ORTAK.ad,
      tur: ORTAK.tur,
      baslangicFiyati: 'Başlangıç fiyatı',
      yayimlandi: 'Yayımlandı',
      kalkislar: 'Kalkışlar',
    },
  },

  /**
   * Denetim maddesi B5: "Webshop senkron ekranı". mbdtravel.com'un push ettiği ve CRM'in kendi
   * veritabanında tuttuğu asıl kayıt — müşteri, sipariş, destek talebi. Yukarıdaki dört sekmeden
   * farkı `sekme.webKayitlari` üstündeki notta.
   */
  webKayitlari: {
    /**
     * Siteden gelen kayit ana kayda baglanir (Ürün Sahibi kararı, 27.08.2026).
     *
     * Bu panel **ilk** sırada duruyor ve tek sebebi var: bu projede dokuz kez tekrar eden kusur,
     * bir şeyin yazılıp hiçbir yerden görülmemesi. Eşleşmenin emin olmadığı satır burada duruyor,
     * sayılıyor ve bir karar bekliyor.
     */
    bag: {
      baslik: 'Karar bekleyen site kayıtları',
      aciklama:
        'Siteden gelen kayıt ana müşteri kaydına bağlanır. Ölçü yalnız normalleştirilmiş e-posta: makine ada bakıp iki kaydı birleştirmiyor, çünkü iki farklı insanı tek müşteri yapmak geri alınamaz. Emin olmadığı her satır buraya düşüyor.',
      yenile: 'Yenile',
      rozet: (acik: string) => `${acik} kayıt karar bekliyor`,
      hicYok: 'Karar bekleyen site kaydı yok.',
      sayac: (gosterilen: string, toplam: string) => `${toplam} kaydın ${gosterilen} tanesi`,
      durumEtiketi: { OPEN: 'Karar bekliyor', RESOLVED: 'Karar verildi', NONE: 'Bağlı' },
      sebep: {
        NO_MASTER_RECORD: 'Ana kayıt yok',
        NAME_MISMATCH: 'Ad tutmuyor',
        NAME_CANDIDATE: 'Aynı adla bir kayıt var',
      },
      bagliDegil: 'Bağlı değil',
      bagliyor: (ad: string, nasil: string) => `${ad} · ${nasil}`,
      nasil: { EMAIL: 'e-posta eşleşmesi', MANUAL: 'temsilci kararı' },
      oneriBaslik: 'Öneriler (şu an hesaplandı)',
      oneriYok: 'Şu an önerilebilecek bir ana kayıt yok. Ana kaydı Satış ekranından açın, sonra buraya dönün.',
      oneriEpostaEslesmesi: 'adres bu kayıtta',
      oneriAyniAd: 'aynı ad',
      tarama:
        'Aynı adı taşıyan kayıt taraması tavana çarptı: "aday yok" cevabı burada bir kanıt değil.',
      bagla: 'Bu kayda bağla',
      elleKimlik: 'Müşteri kimliği',
      elleBagla: 'Bağla',
      not: 'Not',
      kapat: 'Bağlamadan kapat',
      bagiKaldir: 'Bağı kaldır ve kapat',
      notGerekli: 'Kapatma gerekçesini yazın: gerekçesiz bir kapatma yalnız listeyi temizler.',
      cozuldu: (kim: string, ne: string) => `${kim} kapattı: ${ne}`,
      sutun: {
        siteKaydi: 'Site kaydı',
        anaKayit: 'Ana kayıt',
        durum: ORTAK.durum,
        sebep: 'Sebep',
        karar: 'Karar',
      },
    },
    /**
     * Beşinci kenar: ön talep bekleme listesi. Tedarikçi turu kestiğinde rezervasyonun yerine
     * geçen üç alanlı form buraya düşüyor ve **ürün tekrar açıldığında bu kişilere haber
     * gidecek**. O yüzden kaybolmaması gereken şey talebin kendisi değil, işlenip işlenmediği.
     */
    onTalep: {
      baslik: 'Ön talep bekleme listesi',
      aciklama:
        "Tedarikçi turu kestiğinde rezervasyon kutusunun yerine üç alanlı bir form geçiyor: telefon, kişi sayısı, gitmek istediği tarih. E-posta hiç sorulmuyor. Ürün tekrar açıldığında bu kişilere haber gidecek, o yüzden liste en eskiden başlayarak işleniyor. Fiyat talepleri de bu listede: turda çocuk ya da bebek fiyatı olmadığı için ödeme kapanan müşteri adını, e-postasını ve telefonunu bıraktı ve aranmayı bekliyor. WhatsApp teklifleri de burada: müşteri seçimini yapıp WhatsApp'a geçti, telefonu mesajıyla gelecek.",
      yenile: 'Yenile',
      rozet: (bekleyen: string) => `${bekleyen} ön talep bekliyor`,
      hicYok: 'Bu süzgeçte ön talep yok.',
      sayac: (gosterilen: string, toplam: string) => `${toplam} talebin ${gosterilen} tanesi`,
      urunSuzgeci: 'Ürün adresi',
      urunSuzgeciAciklama: 'Bir ürün tekrar açıldıysa adresini yazın: kimlere haber gideceği bu listedir.',
      suz: 'Süz',
      temizle: 'Temizle',
      durumEtiketi: { WAITING: 'Bekliyor', CONTACTED: 'Ulaşıldı', CLOSED: 'Kapandı' },
      // On neden, beş kaynaktan. Boş bırakılan tek bir değer, satış ekranında boş bir hücre
      // demek: talep listede görünür ama niye açıldığı görünmez, ve satışın ilk sorusu tam olarak
      // odur. Ölçüldü 27.08.2026: çapadan gelen ilk talep bu kümede yoktu.
      sebep: {
        TAKVIM_KAPALI: 'Açık tarih kalmadı',
        TEDARIKCI_CEKTI: 'Tedarikçi çekti',
        SEASONAL_CLOSURE: 'Mevsimlik kapalı',
        SUPPLIER_MAINTENANCE: 'Tedarikçi bakımda',
        NOT_YET_SOURCED: 'Tedarikçiye hiç bağlanmadı',
        CAPA_KATALOGDA_YOK: 'Katalogda yok',
        CAPA_EYLEM_UYUSMAZLIGI: 'Katalogda var, bu deneyim yok',
        CAPA_URUN_YOK: 'Ürün henüz açılmadı',
        KAPASITE_DOLU: 'Seansların hepsi dolu',
        FIYAT_EKSIK: 'Çocuk ya da bebek fiyatı yok',
        WHATSAPP_TEKLIF: 'WhatsApp teklifi',
      },
      sebepHamle: {
        TAKVIM_KAPALI: 'Yeni tarih açılınca arayın.',
        TEDARIKCI_CEKTI: 'Benzer bir tur önerin.',
        SEASONAL_CLOSURE: 'Sezon açılışında arayın.',
        SUPPLIER_MAINTENANCE: 'Tedarikçiden açılış tarihini isteyin.',
        NOT_YET_SOURCED: 'Talep birikiyor: tedarikçi arayın.',
        CAPA_KATALOGDA_YOK: 'Talep birikiyor: tedarikçi arayın.',
        CAPA_EYLEM_UYUSMAZLIGI: 'Destinasyon elimizde, bu deneyimi tedarik edin.',
        CAPA_URUN_YOK: 'Ürün açıldığında arayın.',
        KAPASITE_DOLU: 'Ek seans ya da ek araç için operasyona sorun, sonra arayın.',
        FIYAT_EKSIK: 'Müşteri ödemeye hazır: fiyatı tedarikçiden alın ve aynı gün arayın.',
        WHATSAPP_TEKLIF: "Müşteri WhatsApp'tan yazacak: mesajdaki bağlantıyı bu satırla eşleyin ve aynı gün dönün.",
      },
      nedenSuzgeci: 'Neden',
      tumNedenler: 'Tüm nedenler',
      /**
       * Fiyat talebi (`FIYAT_EKSIK`, Ürün Sahibi kararı 30.09.2026). Müşteri çocuk ya da bebek ekledi,
       * kalkışta o yaş için fiyat yok, sitede ödeme kapandı ve müşteri "temsilcimiz sizi arayacak"
       * ekranını gördü. Satışın bu satırda görmesi gereken her şey burada: kim, kaç kişi, hangi
       * kalkış, yetişkinler için ne tutuyor ve neyin sorulacağı.
       */
      fiyatEksik: {
        baslik: 'Fiyat talebi',
        irtibat: 'İrtibat',
        dagilim: 'Yolcular',
        kalkis: 'Kalkış',
        yetiskinTutari: 'Yetişkinler için hesaplanan tutar',
        gelmedi: 'Gelmedi',
        yetiskin: (adet: string) => `${adet} yetişkin`,
        cocuk: (adet: string) => `${adet} çocuk`,
        bebek: (adet: string) => `${adet} bebek`,
        not: {
          cocuk: 'Çocuk fiyatı sorulacak.',
          bebek: 'Bebek fiyatı sorulacak.',
          ikisi: 'Çocuk ve bebek fiyatı sorulacak.',
        },
      },
      /**
       * WhatsApp kısa teklifi (`WHATSAPP_TEKLIF`, v3 Wow W13, 02.10.2026). Müşteri tur sayfasında
       * seçimini yaptı ve WhatsApp'a geçti; mesajında teklifin bağlantısı var, telefonu bu satırda
       * yok. Satış satırı bağlantının kimliğiyle eşliyor ve teklifin kendisini burada okuyor.
       */
      whatsappTeklifi: {
        baslik: 'WhatsApp teklifi',
        teklifNo: 'Teklif numarası',
        baglanti: 'Mesajdaki bağlantı',
        kuyruk: 'v3 kuyruk kimliği',
        dagilim: 'Yolcular',
        kalkis: 'Kalkış',
        toplam: 'Müşterinin gördüğü toplam',
        telefonYok: "Telefon yok, müşteri WhatsApp'tan yazacak",
        gelmedi: 'Gelmedi',
        not: 'Müşteri bu bağlantıyla yazınca numarasını not alanına yazın.',
        arama: 'Teklif numarası ya da bağlantı',
        aramaAciklama:
          'WhatsApp mesajındaki bağlantıyı, WAT numarasını ya da v3 kuyruk kimliğini yapıştırın: teklifin satırı tek başına listelenir.',
        bul: 'Bul',
        aramaGecersiz: 'Bu bir teklif numarası ya da teklif bağlantısı değil.',
      },
      kisi: (adet: string) => `${adet} kişi`,
      anaKayitYok: 'Bu numara bir ana kayıtta yok',
      ulasildi: 'Ulaşıldı olarak işaretle',
      kapat: 'Kapat',
      not: 'Not',
      notGerekli: 'Gerekçe yazın: gerekçesiz bir işaretleme yalnız listeyi temizler.',
      islendi: (kim: string, ne: string) => `${kim}: ${ne}`,
      sutun: {
        talep: 'Talep',
        urun: 'Ürün',
        neden: 'Neden',
        durum: ORTAK.durum,
        islem: 'İşlem',
      },
    },

    musteriler: {
      baslik: 'Müşteri kaydı ara',
      aciklama:
        'Liste değil arama: web sitesi müşterisini bulmanın tek yolu e-posta ya da site kimliğiyle sormak, ikisinden en az biri gerekiyor.',
      eposta: ORTAK.eposta,
      v3Kimligi: 'Site kimliği',
      ara: ORTAK.ara,
      girinmedi: 'E-posta ya da site kimliğinden en az birini girin.',
      bulunamadi: 'Bu bilgiyle kayıtlı bir web sitesi müşterisi yok.',
      alan: {
        adSoyad: 'Ad soyad',
        eposta: ORTAK.eposta,
        v3Kimligi: 'Site kimliği',
        dil: 'Dil',
        kayitTarihi: 'Kayıt tarihi',
        sonGorulme: 'Son görülme',
      },
    },
    siparisler: {
      baslik: 'Web siparişleri',
      tumu: 'Tümü',
      sayac: (gosterilen: string, toplam: string) => `${toplam} siparişin ${gosterilen} tanesi`,
      sutun: {
        referans: 'Referans',
        durum: ORTAK.durum,
        toplam: ORTAK.toplam,
        musteri: ORTAK.musteri,
        eposta: ORTAK.eposta,
        siparisAni: 'Sipariş anı',
        odendi: 'Ödendi',
      },
      /**
       * Denetim madde B5, detay eksiği: liste satırı bir web siparişinin içeriğini göstermiyordu.
       * `api.webOrder(reference)` CRM'in kendi veritabanından siparişin kalemlerini ve açtığı
       * yerine getirme görevlerini döndürüyor; bu yüzden burada `Provenance` yok, v3'e sorulmuyor.
       */
      detay: {
        // v3 göç 0377: müşterinin ödeme öncesi yazdığı not. Tedarikçiye iletilecek bir istek.
        ozelTalepBaslik: 'Müşterinin özel talebi',
        ozelTalepYok: 'Müşteri özel talep yazmadı.',
        // v3 göç 0365: "firma adına fatura" kutusu.
        kurumsalFaturaBaslik: 'Kurumsal fatura',
        kurumsalFatura: { firma: 'Firma adı', vergiDairesi: 'Vergi dairesi', vergiNo: 'Vergi numarası', adres: 'Fatura adresi' },
        // 30.09.2026: müşteriye açılan belge v3 üye portalına gönderiliyor.
        belgeler: {
          baslik: 'Belgeler',
          aciklama:
            'Voucher ve tedarikçi teyidi varsayılan olarak müşterinin üye portalına gönderilir. Diğer belgeler yalnız işaretlenirse gider. Ödeme kanıtı ve sözleşme hiçbir zaman gönderilmez.',
          yok: 'Bu siparişe yüklenmiş belge yok.',
          gonderimKapali: 'Üye portalına gönderim kapalı. Belgeler kuyrukta bekliyor, gönderim açılınca gider. Ayar:',
          gonderimAyari: 'V3_BELGE_ITME=acik',
          v3Tanimsiz: 'Web sitesi bağlantısı tanımlı değil; belgeler kuyrukta bekliyor.',
          sutun: { dosya: 'Dosya', tur: 'Tür', kalem: 'Kalem', musteri: 'Müşteriye açık', durum: 'Gönderim', yuklendi: 'Yüklendi', islem: 'İşlem' },
          tur: {
            VOUCHER: 'Voucher',
            SUPPLIER_CONFIRMATION: 'Tedarikçi teyidi',
            OTHER: 'Diğer',
            PAYMENT_EVIDENCE: 'Ödeme kanıtı',
            CONTRACT: 'Sözleşme',
          } as Readonly<Record<string, string>>,
          musteriyeKapali: 'Müşteriye kapalı',
          silindi: 'Silindi',
          indir: 'İndir',
          yenidenGonder: 'Yeniden gönder',
          sil: 'Sil',
          silOnay: 'Belge silinsin mi? Müşteriye gönderildiyse üye portalından da geri çekilir.',
          yeniSurum: 'Yeni sürüm',
          yeniSurumIptal: 'Yeni sürümden vazgeç',
          yeniSurumNotu: (dosya: string) => `${dosya} için yeni sürüm yükleniyor. Müşteri eski sürümün yerine bunu görecek.`,
          yukleBaslik: 'Belge yükle',
          yukleTur: 'Belge türü',
          yukleKalem: 'Kalem (isteğe bağlı)',
          tumSiparis: 'Bütün sipariş',
          yukleMusteri: 'Müşteriye göster (üye portalı)',
          yukleMusteriYasak: 'Bu tür müşteriye hiçbir zaman gösterilmez.',
          yukleDosya: 'Dosya',
          yukle: 'Yükle',
          yukleniyor: 'Yükleniyor',
          dosyaSec: 'Önce bir dosya seçin.',
          dosyaOkunamadi: 'Dosya okunamadı.',
          sonHata: 'Son hata',
        },
        kalemlerBaslik: 'Satın alınan ürünler',
        vizeOperasyon: 'Vize başvurularını operasyonda aç',
        urunAdiYok: 'Ürün adı kayıtta bulunmuyor',
        kayitAyrintilari: 'Kayıt ayrıntıları',
        urunKodu: 'Ürün kodu',
        kalemReferansi: 'Sipariş kalemi referansı',
        ozet: { durum: 'Sipariş durumu', tarih: 'Sipariş tarihi (BAE)', odeme: 'Ödeme tarihi (BAE)', toplam: 'Sipariş toplamı' },
        kalemYok: 'Bu siparişte kalem yok.',
        kalemSutun: {
          kalem: 'Kalem',
          urun: 'Ürün',
          kalkis: 'Kalkış',
          adet: 'Adet',
          birimFiyat: 'Birim fiyat',
        },
        gorevlerBaslik: 'Operasyon işleri',
        gorevYok: 'Bu sipariş için açılmış operasyon işi yok.',
        gorevSutun: {
          kalem: 'Kalem',
          durum: ORTAK.durum,
          urun: 'Ürün',
          kalkis: 'Kalkış',
          adet: 'Adet',
          acildi: 'Açıldı',
          tamamlandi: 'Tamamlandı',
        },
      },
    },
    destek: {
      baslik: 'Destek talepleri',
      tumu: 'Tümü',
      sayac: (gosterilen: string, toplam: string) => `${toplam} talebin ${gosterilen} tanesi`,
      sutun: {
        referans: 'Referans',
        konu: 'Konu',
        durum: ORTAK.durum,
        siparis: ORTAK.siparis,
        acildi: 'Açıldı',
      },
      /**
       * Denetim madde B5, detay eksiği: `api.webSupportCase(reference)` bu turda liste
       * satırındakiyle aynı sözleşmeyi döndürüyor, ama listede olmayan e-posta ve ad soyadı da
       * taşıyor. Uydurma alan yok, sözleşmede olan gösteriliyor.
       */
      detay: {
        alan: {
          eposta: ORTAK.eposta,
          adSoyad: 'Ad soyad',
          konu: 'Konu',
          durum: ORTAK.durum,
          siparis: ORTAK.siparis,
          acildi: 'Açıldı',
        },
      },
    },
    sayfalama: {
      sayfa: (sayfa: string) => `${ORTAK.sayfa} ${sayfa}`,
    },
  },

  /**
   * Yalnız v3 sözleşmesinde geçen kod değerlerinin karşılıkları.
   *
   * `ortak.ts` içindeki `DURUM_ADI` CRM'in kendi defterinin durumlarını taşıyor; buradakiler
   * o deftere hiç girmiyor, yalnız v3'ten okunan siparişte ve faturada görünüyor. Ortak
   * tabloya konsaydı, CRM ekranlarında hiç karşılaşılmayacak durumlar oraya karışırdı.
   *
   * `AUTHORIZED`in Türkçesi 24.08.2026'da karara bağlandı: **provizyon alındı**. Terim ürün
   * sahibinin kendi kelimesi ve bu dosyanın başka bir yerinde (`provizyonNotu`) zaten aynı
   * şekilde yazılıydı; kod değerinin ekranda çıplak durmasının sebebi kalmadı.
   *
   * `CAPTURED` hâlâ bilerek yok: karşılığı karara bağlanmadı ve uydurulmuş bir karşılık, kod
   * değerinden kötüdür. Bugün ekranda tahsil edilen tutar zaten ayrı bir rakam olarak duruyor.
   */
  durum: {
    PENDING_PAYMENT: 'Ödeme bekliyor',
    AUTHORIZED: 'Provizyon alındı',
    PAID: 'Ödendi',
    REFUNDED: 'İade edildi',
    NONE: ORTAK.yok,
  } as Readonly<Record<string, string>>,

  /** Yolcu türleri. Kod değeri v3'te olduğu gibi kalıyor. */
  yolcuTuru: {
    ADULT: 'Yetişkin',
    CHILD: 'Çocuk',
    INFANT: 'Bebek',
  } as Readonly<Record<string, string>>,

  /**
   * Ondalık ayırıcı, kur oranı için.
   *
   * Kur `Intl` ile biçimlenmiyor: v3 oranı ölçekli tam sayı olarak gönderiyor ve bölme yapılırsa
   * kayan noktalı sayı son basamağı kaydırabiliyor. Metin tam sayıdan kuruluyor, ayırıcı da bu
   * yüzden elle konuyor.
   */
  ondalikAyirici: ',',
} as const;
